<template>
	<Modal width="900px">
		<template #modal-title>
			<IconButton
				v-if="showImportView"
				data-testid="back-button"
				@click="showImportView = false"
			>
				mdi-arrow-left
			</IconButton>
			<span class="title">
				{{ showImportView ? 'Import Integration Configuration' : 'Create New Integration' }}
			</span>
			<v-spacer />
			<IconButton
				data-testid="close-button"
				@click="$emit('close')"
			>
				mdi-close
			</IconButton>
		</template>

		<template #modal-content>
			<!-- Options Selection View -->
			<div
				v-if="!showImportView"
				class="service-creation-options"
			>
				<p class="subtitle-1 text--secondary mb-6 text-center">
					Choose how you'd like to set up your new integration for {{ systemName }}
				</p>

				<v-row class="justify-center align-stretch">
					<v-col
						v-if="hasAvailableTemplates"
						cols="12"
						sm="6"
						:md="hasAvailableTemplates ? '4' : '5'"
						class="d-flex"
					>
						<OverviewCard
							title="Use Template"
							description="Start with a pre-configured integration template that matches your system type."
							icon="mdi-file-document-multiple"
							use-grey
							data-testid="template-option"
							@overview="handleTemplateSelect"
						/>
					</v-col>

					<v-col
						cols="12"
						sm="6"
						:md="hasAvailableTemplates ? '4' : '5'"
						class="d-flex"
					>
						<OverviewCard
							title="Custom Setup"
							description="Build your integration from scratch with complete control over all settings, endpoints, and field mappings."
							icon="mdi-cog"
							use-grey
							data-testid="custom-option"
							@overview="handleCustomSelect"
						/>
					</v-col>

					<v-col
						cols="12"
						sm="6"
						:md="hasAvailableTemplates ? '4' : '5'"
						class="d-flex"
					>
						<OverviewCard
							title="Import Configuration"
							description="Import an existing integration configuration using JSON format for quick setup."
							icon="mdi-file-import"
							use-grey
							data-testid="import-option"
							@overview="handleImportSelect"
						/>
					</v-col>
				</v-row>
			</div>

			<!-- Import Configuration View -->
			<div
				v-else
				class="import-configuration-view"
			>
				<p class="subtitle-1 text--secondary mb-6">
					Upload a JSON configuration file exported from another integration to quickly recreate your integration setup for {{ systemName }}.
				</p>

				<!-- File Upload Section -->
				<div class="file-upload-section mb-6">
					<FileInput
						label="Select JSON Configuration File"
						accept=".json"
						:disabled="isImporting"
						select-file-only
						@selectFile="handleFileUpload"
					/>
				</div>
				<div
					v-if="importValidationError"
					class="import-error mt-3"
				>
					<v-alert
						type="error"
						dense
						outlined
					>
						<pre class="import-error__pre">{{ importValidationError }}</pre>
					</v-alert>
				</div>
			</div>
		</template>
		<template
			v-if="showImportView"
			#modal-footer
		>
			<SecondaryActionButton
				class="mr-3"
				data-testid="cancel-import-button"
				@click="cancelImport"
			>
				Cancel
			</SecondaryActionButton>
			<v-spacer />
			<PrimaryActionButton
				:disabled="!isValidImportData || isImporting"
				:loading="isImporting"
				data-testid="create-from-import-button"
				@click="createFromImport"
			>
				Create from Import
			</PrimaryActionButton>
		</template>
	</Modal>
</template>

<script>
import Modal from '../../../../../shared/components/modal.vue'
import IconButton from '../../../../../shared/components/icon-button.vue'
import OverviewCard from '../../../../../shared/components/overview-card.vue'
import PrimaryActionButton from '../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../shared/components/secondary-action-button.vue'
import FileInput from '../../../../../shared/components/file-input.vue'
import { validateServiceImportData } from './service-integration-validator.js'
import { postIntegrationService } from '../../../../../shared/utils/api/connector-service.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import { appendJsonErrorContext } from '../../../../shared/utils/json-error-formatting.js'

export default {
	name: 'service-creation-options-modal',
	components: {
		Modal,
		IconButton,
		OverviewCard,
		PrimaryActionButton,
		SecondaryActionButton,
		FileInput
	},
	props: {
		systemName: {
			type: String,
			required: true
		},
		hasAvailableTemplates: {
			type: Boolean,
			default: false
		},
		systemIntegrationId: {
			type: Number,
			required: true
		}
	},
	data () {
		return {
			showImportView: false,
			importJsonData: '',
			selectedFile: null,
			isImporting: false,
			isValidImportData: false,
			importValidationError: null,
			cleanedImportData: null
		}
	},
	watch: {
		// Watch for changes to importJsonData and validate it
		importJsonData (newValue) {
			// Only validate, don't manipulate the JSON content
			this.validateImportData()
		}
	},
	methods: {
		handleTemplateSelect () {
			this.$emit('select-template', this.systemName)
		},
		handleCustomSelect () {
			this.$emit('select-custom')
		},
		handleImportSelect () {
			this.showImportView = true
		},
		handleFileUpload (file) {
			if (!file) {
				return
			}

			// Validate file type
			if (!file.name.toLowerCase().endsWith('.json')) {
				this.importValidationError = 'Please select a valid JSON file (.json extension required)'
				this.selectedFile = null
				this.isValidImportData = false
				return
			}

			// Read file content
			const reader = new FileReader()
			reader.onload = e => {
				try {
					const content = e.target.result
					// Validate that it's valid JSON by trying to parse it
					try {
						JSON.parse(content)
					} catch (parseError) {
						this.importValidationError = appendJsonErrorContext('Invalid JSON file: ' + parseError.message, content, parseError)
						this.selectedFile = null
						this.importJsonData = ''
						this.isValidImportData = false
						return
					}
					// If successful, set the content and validate
					this.importJsonData = content
					this.validateImportData()
				} catch (error) {
					this.importValidationError = 'Error processing file: ' + error.message
					this.selectedFile = null
					this.importJsonData = ''
					this.isValidImportData = false
				}
			}
			reader.onerror = () => {
				this.importValidationError = 'Error reading file. Please try again.'
				this.selectedFile = null
				this.isValidImportData = false
			}
			reader.readAsText(file)
		},
		validateImportData () {
			const result = validateServiceImportData(this.importJsonData)

			this.isValidImportData = result.isValid
			this.importValidationError = result.error ? appendJsonErrorContext(result.error, this.importJsonData) : null

			// Store cleaned data for use in createFromImport
			if (result.isValid && result.data) {
				this.cleanedImportData = result.data
				this.importValidationError = null
			} else {
				this.cleanedImportData = null
			}
		},
		async createFromImport () {
			if (!this.isValidImportData || !this.cleanedImportData) return

			this.isImporting = true

			try {
				const configData = { ...this.cleanedImportData }
				// Ensure the service is linked to the current system
				configData.integrationId = this.systemIntegrationId
				// Only stringify requestBody if it's not already a string
				if (typeof configData.requestBody !== 'string') {
					configData.requestBody = JSON.stringify(configData.requestBody, null, 2)
				}

				await postIntegrationService(configData)
				showSnackbar('Integration created successfully from import')
				this.$emit('import-success')
			} catch (error) {
				this.importValidationError = appendJsonErrorContext('Failed to process configuration: ' + error.message, this.importJsonData, error)
				showSnackbar('Failed to create integration from import', 'error')
			} finally {
				this.isImporting = false
			}
		},
		cancelImport () {
			this.showImportView = false
			this.importJsonData = ''
			this.selectedFile = null
			this.isValidImportData = false
			this.importValidationError = null
			this.cleanedImportData = null
		}
	}
}
</script>

<style scoped>
.service-creation-options {
  min-height: 200px;
}

.options-container {
  max-width: 100%;
}

.option-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 350px;
  max-width: 400px;
}

.option-wrapper >>> .overview-card {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.option-wrapper >>> .overview-card .v-card__text {
  flex-grow: 1;
}

.file-upload-section {
  border: 1px dashed var(--global-border-color);
  border-radius: var(--radius-sm, 4px);
  padding: 16px;
  background-color: var(--global-main-background-color);
}

.import-actions {
  border-top: 1px solid var(--global-main-background-color);
  padding-top: 16px;
}

.import-error {
  max-width: 600px;
}
</style>
